import React, { useEffect, useCallback, useState, useRef } from 'react';
import {
    Stack,
    FormControl,
    FormLabel,
    Input,
    Button,
    Box,
    Text,
    Select,
    Textarea,
    NumberInput,
    NumberInputField,
    Flex,
    HStack,
    VStack,
    RadioGroup,
    Radio,
    SimpleGrid,
    useToast,
} from '@chakra-ui/react';
import { FiTag } from 'react-icons/fi';
import { toZonedTime, format } from 'date-fns-tz';
import { addDays } from 'date-fns';
import axios from 'axios';

const FREQUENCY_ORDER = { weekly: 0, biweekly: 1, monthly: 2 };

function frequencyKey(opt) {
    return String(opt || '').toLowerCase().replace(/[\s_-]/g, '');
}

function frequencyLabel(opt) {
    const key = frequencyKey(opt);
    if (key === 'weekly') return 'Every Week';
    if (key === 'biweekly') return 'Every 2 Weeks';
    if (key === 'monthly') return 'Every month';
    return String(opt);
}

function sortFrequencies(options) {
    return [...(options || [])].sort(
        (a, b) => (FREQUENCY_ORDER[frequencyKey(a)] ?? 9) - (FREQUENCY_ORDER[frequencyKey(b)] ?? 9)
    );
}

/**
 * SchedulePage — Extracted scheduling step for the unified order flow.
 * Handles: pickup/dropoff dates & times, delivery method (Uber/Driver),
 * frequency (Subscribe & Save), promo code, special instructions, laundry bags.
 */
export default function SchedulePage({
    orderType,
    setOrderType,
    subscribeSaveAvailable = false,
    cartIsBagOnly = false,
    minTurnaroundHours = null,
    sameDayCutoffHours = 4,
    customerTimeSelectionEnabled = true,
    closureDates = [],
    allowedPickupDays = null,
    allowedDeliveryDays = null,
    subscriptionDiscount = 0,
    recurringDiscount = 0,
    pickupDate,
    setPickupDate,
    pickupTime,
    setPickupTime,
    dropoffDate,
    setDropoffDate,
    dropoffTime,
    setDropoffTime,
    earliestDropoffDate = "",
    dropoffDateLabel = "Drop-off Date",
    dryCleanDropoffDate = "",
    setDryCleanDropoffDate,
    dryCleanMinDate = "",
    pickupService,
    setPickupService,
    dropoffService,
    setDropoffService,
    frequency,
    setFrequency,
    promoCode,
    setPromoCode,
    specialInstructions,
    setSpecialInstructions,
    setSaveSpecialInstructions,
    laundryBags,
    setLaundryBags,
    deliveryTimeSlots,
    deliveryTimeInterval,
    laundryTimeZone,
    laundryFrequency,
    frequencyPromotions,
    promoDescriptionMessage,
    setPromoDescriptionMessage,
    uberEnv,
    uberExists,
    setUberExists,
    laundryAddress,
    address,
    uberPickupFrequency,
    setUberPickupFrequency,
    uberDropoffFrequency,
    setUberDropoffFrequency,
    doordashExists = false,
    missedPickupFee = 0,
    courierHours = null,
    doordashPickupFrequency = false,
    setDoordashPickupFrequency = () => {},
    doordashDropoffFrequency = false,
    setDoordashDropoffFrequency = () => {},
    laundryId,
    customerId,
    onContinue,
    onBack,
}) {
    const toast = useToast();
    const userAuthToken = localStorage.getItem('idToken');
    const initialSpecialInstructionsRef = useRef(specialInstructions);

    // Promo-related state
    const [localPromoCode, setLocalPromoCode] = useState(promoCode || '');
    const [isPromoValidating, setIsPromoValidating] = useState(false);
    const [isPromoValid, setIsPromoValid] = useState(!!promoCode);
    const [isFreqPromoApplied, setIsFreqPromoApplied] = useState(false);

    // Uber estimate state
    const [pickupEstimate, setPickupEstimate] = useState(null);
    const [dropoffEstimate, setDropoffEstimate] = useState(null);

    // Pickup mode state
    const [pickupMode, setPickupMode] = useState('scheduled');
    const pickupModeRef = useRef(pickupMode);
    useEffect(() => { pickupModeRef.current = pickupMode; }, [pickupMode]);

    // Tracks the pickup date the return was last aligned to, so a pickup-date
    // change re-anchors the return to the new earliest eligible date in BOTH
    // directions (moving pickup earlier must pull the return earlier too, not
    // just push it later).
    const prevPickupDateRef = useRef(null);

    // ─── Utility Functions ───

    const getDateInTimeZone = (date, timeZone) => {
        const zonedDate = toZonedTime(date, timeZone || 'America/New_York');
        return format(zonedDate, 'yyyy-MM-dd', { timeZone: timeZone || 'America/New_York' });
    };

    const getTodayInLaundryTZ = () => {
        const timeZone = laundryTimeZone || 'America/New_York';
        const now = new Date();
        const zoned = toZonedTime(now, timeZone);
        return format(zoned, 'yyyy-MM-dd', { timeZone });
    };

    // Earliest allowed return is driven by the slowest service in the cart:
    // ceil(turnaround / 24) days after pickup. When a turnaround is explicitly
    // configured (including 0 = same-day) we honor it with a floor of 0 days,
    // so a Same-Day service can be returned on the pickup date. When NO service
    // in the cart has a configured turnaround (minTurnaroundHours == null), we
    // keep the historical next-day (1 day) minimum.
    const _hasTurnaround = minTurnaroundHours != null && Number.isFinite(Number(minTurnaroundHours));
    const _baseMinDropoffDays = _hasTurnaround
        ? Math.max(0, Math.ceil(Number(minTurnaroundHours) / 24))
        : 1;

    // Same-day feasibility: a same-day service (base min = 0 days) is only
    // returnable on the pickup date if, on that day, there are at least
    // `sameDayCutoffHours` hours between the END of the selected pickup window
    // and the shop's closing time (that day's slot endTime). Otherwise same-day
    // is not offered and we push the earliest return to the next day.
    const _parseHour = (hhmm) => {
        if (!hhmm || typeof hhmm !== 'string') return null;
        const [h, m] = hhmm.split(':').map(Number);
        if (!Number.isFinite(h)) return null;
        return h + (Number.isFinite(m) ? m / 60 : 0);
    };
    // End hour of the selected pickup window ("09:00 - 11:00" -> 11).
    const _pickupWindowEndHour = (() => {
        if (!pickupTime || typeof pickupTime !== 'string') return null;
        const parts = pickupTime.split('-');
        return _parseHour((parts[1] || parts[0] || '').trim());
    })();
    // Close hour for the pickup day's slot.
    const _pickupDayCloseHour = (() => {
        if (!pickupDate || !deliveryTimeSlots) return null;
        const tzName = laundryTimeZone || 'America/New_York';
        const day = format(toZonedTime(new Date(`${pickupDate}T00:00`), tzName), 'EEEE', { timeZone: tzName });
        const slot = deliveryTimeSlots.find((s) => s.day === day);
        return slot ? _parseHour(slot.endTime) : null;
    })();
    // Same-day is blocked when the base allows it (0 days) but the remaining
    // window before close is under the tenant's cutoff.
    const sameDayBlocked = (
        _baseMinDropoffDays === 0 &&
        _pickupWindowEndHour != null &&
        _pickupDayCloseHour != null &&
        (_pickupDayCloseHour - _pickupWindowEndHour) < (Number(sameDayCutoffHours) || 0)
    );
    const minDropoffDays = sameDayBlocked ? 1 : _baseMinDropoffDays;

    // ─── Eligible-day helpers (closures + operating weekdays) ───
    // Set of closure 'YYYY-MM-DD' strings for O(1) lookups.
    const _closureSet = (() => {
        const s = new Set();
        (closureDates || []).forEach((d) => { const v = String(d || '').trim(); if (v) s.add(v); });
        return s;
    })();
    // Weekday names the shop operates on. When the validated address maps to a
    // service zone, that zone's days take precedence (narrows the picker to that
    // zone). Otherwise use the tenant's global days from delivery_time_slots.
    // Empty => treat every weekday as operating (fail-open to prior behavior).
    const _operatingDayNames = (() => {
        const s = new Set();
        if (Array.isArray(allowedPickupDays) && allowedPickupDays.length > 0) {
            allowedPickupDays.forEach((d) => { if (d) s.add(String(d)); });
            return s;
        }
        (deliveryTimeSlots || []).forEach((slot) => { if (slot && slot.day) s.add(String(slot.day)); });
        return s;
    })();
    // Days the shop DELIVERS on, for the return/drop-off date. This must NOT
    // use allowedPickupDays: service zones restrict which days a customer's
    // area is PICKED UP, not when their order can be returned. Using pickup-zone
    // days here wrongly pushed the earliest return past valid delivery days
    // (e.g. a Mon/Wed pickup zone shoved a 2-day return from Wed out to Sunday).
    //
    // When the validated address maps to a zone that has its OWN delivery days
    // (allowedDeliveryDays), those take precedence — intersected with the global
    // delivery schedule so a chosen day still has actual delivery hours. No zone
    // delivery days -> fall back to the tenant's global delivery days.
    const _deliveryDayNames = (() => {
        const globalDays = new Set();
        (deliveryTimeSlots || []).forEach((slot) => { if (slot && slot.day) globalDays.add(String(slot.day)); });
        if (Array.isArray(allowedDeliveryDays) && allowedDeliveryDays.length > 0) {
            const zoneDays = allowedDeliveryDays.map((d) => String(d));
            const intersected = zoneDays.filter((d) => globalDays.size === 0 || globalDays.has(d));
            // If the zone's delivery days don't overlap the global schedule at all
            // (misconfig), fall back to global rather than leaving zero eligible days.
            return new Set(intersected.length > 0 ? intersected : Array.from(globalDays));
        }
        return globalDays;
    })();
    const _dayName = (yyyyMmDd) => {
        const tzName = laundryTimeZone || 'America/New_York';
        return format(toZonedTime(new Date(`${yyyyMmDd}T00:00`), tzName), 'EEEE', { timeZone: tzName });
    };
    // A date is eligible when its weekday operates AND it isn't a closure date.
    const isEligibleDay = (yyyyMmDd) => {
        if (!yyyyMmDd) return false;
        if (_closureSet.has(yyyyMmDd)) return false;
        if (_operatingDayNames.size > 0 && !_operatingDayNames.has(_dayName(yyyyMmDd))) return false;
        return true;
    };
    // A return/drop-off date is eligible when the shop DELIVERS that weekday and
    // it isn't a closure. Uses delivery days only (never pickup-zone days).
    const isEligibleDeliveryDay = (yyyyMmDd) => {
        if (!yyyyMmDd) return false;
        if (_closureSet.has(yyyyMmDd)) return false;
        if (_deliveryDayNames.size > 0 && !_deliveryDayNames.has(_dayName(yyyyMmDd))) return false;
        return true;
    };
    // First eligible day on or after the given date (14-day safety cap).
    // `checkFn` decides eligibility — pickup uses isEligibleDay (pickup-zone
    // aware), return uses isEligibleDeliveryDay (delivery days only).
    const nextEligibleDayStr = (yyyyMmDd, checkFn = isEligibleDay) => {
        const tz = laundryTimeZone || 'America/Chicago';
        let d = new Date(`${yyyyMmDd}T00:00:00`);
        for (let i = 0; i <= 14; i++) {
            const s = getDateInTimeZone(d, tz);
            if (checkFn(s)) return s;
            d = addDays(d, 1);
        }
        return yyyyMmDd; // misconfigured tenant — don't loop forever
    };

    // Earliest return: pickup + turnaround days.
    //
    // For time-window tenants (customerTimeSelectionEnabled = true) the raw date
    // is rolled forward past closures / non-DELIVERY weekdays, because the return
    // has to land on a day the tenant actually runs a delivery route.
    //
    // For date-only / notify-later tenants (flag false), the return is simply
    // pickup + turnaround (skipping only closure dates) — we do NOT push it onto
    // a configured delivery weekday, since these tenants schedule routes and
    // notify the customer separately (Option A). This is what makes a Wed +48h
    // order show Friday instead of being shoved to the next delivery day.
    const minDropoffDateStr = (pickup) => {
        if (earliestDropoffDate) return earliestDropoffDate;
        const raw = getDateInTimeZone(addDays(new Date(`${pickup}T00:00:00`), minDropoffDays), laundryTimeZone || 'America/Chicago');
        // Date-only tenants: only skip closures, never require a delivery weekday.
        const checkFn = customerTimeSelectionEnabled
            ? isEligibleDeliveryDay
            : ((s) => !!s && !_closureSet.has(s));
        return nextEligibleDayStr(raw, checkFn);
    };

    const getCurrent2HourSlot = () => {
        const timeZone = laundryTimeZone || 'America/New_York';
        const now = toZonedTime(new Date(), timeZone);
        const roundedMinutes = Math.ceil(now.getMinutes() / 5) * 5;
        now.setMinutes(roundedMinutes);
        now.setSeconds(0);
        now.setMilliseconds(0);
        const start = new Date(now);
        const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
        const startStr = format(start, 'HH:mm');
        const endStr = format(end, 'HH:mm');
        return `${startStr} - ${endStr}`;
    };

    // Couriers don't depend on store drivers, so on-demand follows the shop's
    // courier hours (the server falls back to store hours when none are set).
    const onDemandSlots = Array.isArray(courierHours) ? courierHours : (deliveryTimeSlots || []);

    const isWithinHours = () => {
        const now = toZonedTime(new Date(), laundryTimeZone || 'America/New_York');
        const todayDay = format(now, 'EEEE', { timeZone: laundryTimeZone || 'America/New_York' });
        const slot = onDemandSlots.find((s) => s.day === todayDay);
        if (!slot) return false;
        const [startHour, startMinute] = slot.startTime.split(':').map(Number);
        const [endHour, endMinute] = slot.endTime.split(':').map(Number);
        const start = new Date(now);
        start.setHours(startHour, startMinute, 0, 0);
        const end = new Date(now);
        end.setHours(endHour, endMinute, 0, 0);
        return now >= start && now <= end;
    };

    const canDoInstantPickup = (uberExists || doordashExists) && isWithinHours();
    const bothCouriers = uberExists && doordashExists;

    const to12h = (hhmm) => {
        const [h, m] = String(hhmm || '').split(':').map(Number);
        if (Number.isNaN(h)) return hhmm;
        const suffix = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return m ? `${h12}:${String(m).padStart(2, '0')} ${suffix}` : `${h12} ${suffix}`;
    };
    // Display-only: slot values stay "HH:MM - HH:MM" for the API.
    const slotLabel = (slot) => {
        const parts = String(slot || '').split(' - ');
        return parts.length === 2 ? `${to12h(parts[0])} – ${to12h(parts[1])}` : slot;
    };
    const onDemandUnavailableReason = (() => {
        const tz = laundryTimeZone || 'America/New_York';
        const now = toZonedTime(new Date(), tz);
        const todayDay = format(now, 'EEEE', { timeZone: tz });
        const slot = onDemandSlots.find((s) => s.day === todayDay);
        if (!slot) return 'Not available today. Please schedule a pickup.';
        const [sh, sm] = slot.startTime.split(':').map(Number);
        const nowMin = now.getHours() * 60 + now.getMinutes();
        if (nowMin < sh * 60 + (sm || 0)) {
            return `Available today from ${to12h(slot.startTime)} to ${to12h(slot.endTime)}. Check back at ${to12h(slot.startTime)}, or schedule a pickup.`;
        }
        return `Done for today (available ${to12h(slot.startTime)} – ${to12h(slot.endTime)}). Please schedule a pickup.`;
    })();
    const [instantCourier, setInstantCourier] = useState(uberExists ? 'Uber' : 'DoorDash');
    useEffect(() => {
        if (instantCourier === 'Uber' && !uberExists && doordashExists) setInstantCourier('DoorDash');
        if (instantCourier === 'DoorDash' && !doordashExists && uberExists) setInstantCourier('Uber');
    }, [uberExists, doordashExists, instantCourier]);

    // ─── Time Slot Generation ───

    const getAvailableTimeSlots = useCallback(
        (selectedDate) => {
            if (!selectedDate || !deliveryTimeSlots) return [];
            const tz = laundryTimeZone || 'America/New_York';
            const selectedDateLocal = toZonedTime(new Date(`${selectedDate}T00:00`), tz);
            const selectedDay = format(selectedDateLocal, 'EEEE', { timeZone: tz });
            const timeSlot = deliveryTimeSlots.find((slot) => slot.day === selectedDay);

            if (timeSlot) {
                const start = parseInt(timeSlot.startTime.split(':')[0], 10);
                const end = parseInt(timeSlot.endTime.split(':')[0], 10);
                const timeSlots = [];
                for (let time = start; time < end; time += deliveryTimeInterval) {
                    const startTimeFormatted = `${String(time).padStart(2, '0')}:00`;
                    const endTimeFormatted = `${String(time + deliveryTimeInterval).padStart(2, '0')}:00`;
                    timeSlots.push(`${startTimeFormatted} - ${endTimeFormatted}`);
                }
                return timeSlots;
            }
            return [];
        },
        [deliveryTimeSlots, deliveryTimeInterval, laundryTimeZone]
    );

    // ─── Pickup Mode Effects ───

    useEffect(() => {
        if (pickupMode === 'instant') {
            const currentDate = getTodayInLaundryTZ();
            const instantTime = getCurrent2HourSlot();
            setPickupDate(currentDate);
            setPickupTime(instantTime);
            setPickupService(instantCourier);
        } else {
            if (!pickupService) setPickupService('LaundryDriver');
            // Default pickup is ALWAYS tomorrow (rolled forward to the next
            // eligible pickup day past closures / non-operating days). The
            // customer can still switch to today when it's before the cutoff —
            // the calendar `min` (todayDate) allows that — but we never default
            // them to a same-day pickup.
            const zoneTz = laundryTimeZone || 'America/Chicago';
            const tomorrow = getDateInTimeZone(addDays(new Date(), 1), zoneTz);
            const defaultDate = nextEligibleDayStr(tomorrow);
            if (!pickupDate) setPickupDate(defaultDate);
        }
    }, [pickupMode, laundryTimeZone]);

    useEffect(() => {
        if (pickupMode === 'scheduled') {
            if (!pickupService) setPickupService('LaundryDriver');
            if (!dropoffService) setDropoffService('LaundryDriver');
        }
    }, [pickupMode, pickupService, dropoffService, setPickupService, setDropoffService]);

    // ─── Date/Time Auto-Selection Effects ───

    useEffect(() => {
        if (pickupDate && deliveryTimeSlots && deliveryTimeSlots.length > 0) {
            const availablePickupTimeSlots = getAvailableTimeSlots(pickupDate);
            // Only auto-fill a pickup time window when the tenant lets customers
            // choose one. Date-only tenants (flag off) must NOT get a default
            // window — leave pickupTime blank so no "08:00 - 10:00" is persisted
            // or displayed. The return-date logic below still runs regardless.
            if (customerTimeSelectionEnabled && pickupModeRef.current !== 'instant') {
                if (!pickupTime || !availablePickupTimeSlots.includes(pickupTime)) {
                    if (availablePickupTimeSlots.length > 0) {
                        setPickupTime(availablePickupTimeSlots[0]);
                    } else {
                        setPickupTime('');
                    }
                }
            } else if (!customerTimeSelectionEnabled && pickupTime) {
                // Clear any stale window if the tenant is date-only.
                setPickupTime('');
            }
            // Earliest return honors the cart's slowest service turnaround.
            const minDropoffDate = minDropoffDateStr(pickupDate);
            const pickupChanged = prevPickupDateRef.current !== null
                && prevPickupDateRef.current !== pickupDate;
            prevPickupDateRef.current = pickupDate;
            if (pickupChanged) {
                // The customer moved the pickup date — re-anchor the return to the
                // new earliest eligible date, whether pickup moved earlier or later.
                if (dropoffDate !== minDropoffDate) {
                    setDropoffDate(minDropoffDate);
                }
            } else if (!dropoffDate || new Date(dropoffDate) < new Date(minDropoffDate)) {
                // First mount / restored value: only ensure the return isn't
                // earlier than the minimum (keep a valid later choice as-is).
                setDropoffDate(minDropoffDate);
            }
        }
    }, [customerTimeSelectionEnabled, pickupDate, deliveryTimeSlots, getAvailableTimeSlots, setPickupTime, pickupTime, dropoffDate, setDropoffDate, laundryTimeZone, minDropoffDays]);

    // NOTE: The "recalculate return date when the service SPEED changes" reset
    // lives in the PARENT (LaundryPickupPage), keyed on the cart's turnaround.
    // SchedulePage unmounts/remounts when the customer navigates between steps,
    // so a reset kept here (with a mount-scoped ref) would be skipped on return.
    // The parent owns pickupDate/dropoffDate + turnaround, which persist across
    // steps, so it snaps the return to the earliest eligible date on any service
    // change. The forward-push effect above still applies the same-day cutoff.

    useEffect(() => {
        // Date-only tenants (flag off): never auto-fill a drop-off window; clear
        // any stale value so no default window is persisted/displayed.
        if (!customerTimeSelectionEnabled) {
            if (dropoffTime) setDropoffTime('');
            return;
        }
        if (dropoffDate && deliveryTimeSlots && deliveryTimeSlots.length > 0) {
            const availableDropoffTimeSlots = getAvailableTimeSlots(dropoffDate);
            if (!dropoffTime || !availableDropoffTimeSlots.includes(dropoffTime)) {
                if (availableDropoffTimeSlots.length > 0) {
                    setDropoffTime(availableDropoffTimeSlots[0]);
                } else {
                    setDropoffTime('');
                }
            }
        }
    }, [customerTimeSelectionEnabled, dropoffDate, deliveryTimeSlots, getAvailableTimeSlots, setDropoffTime, dropoffTime, laundryTimeZone]);

    // ─── Frequency / Promo Auto-Apply ───

    useEffect(() => {
        if (frequency) {
            // Only auto-apply frequency promo codes for "Frequency" order type.
            // Subscribe & Save uses subscriptionDiscount from system settings.
            // Precedence (option 2): when the tenant has configured a recurring
            // discount %, it REPLACES the legacy auto-applied frequency promo — so
            // don't attach the promo code; the config % is applied instead (in
            // handlePlaceOrder here and at weigh-in server-side). This keeps them
            // from stacking and mirrors the backend recompute exactly.
            if (orderType !== 'subscribe-save' && !(recurringDiscount > 0)) {
                const promo = frequencyPromotions
                    ? frequencyPromotions.find((p) => p.frequency === frequency)
                    : null;
                if (promo) {
                    setLocalPromoCode(promo.promoCode);
                    setPromoCode(promo.promoCode);
                    setPromoDescriptionMessage(`Promo Applied! ${promo.description}`);
                    setIsPromoValid(true);
                    setIsFreqPromoApplied(true);
                } else {
                    // The selected interval button is the confirmation. A
                    // "You selected" line only repeats it.
                    setPromoDescriptionMessage('');
                }
            } else {
                // Subscribe & Save, or Recurring with a configured discount:
                // no promo auto-apply. Clear any stale auto-applied frequency
                // promo so the config discount is the only recurring savings.
                if (isFreqPromoApplied) {
                    setLocalPromoCode('');
                    setPromoCode('');
                    setIsPromoValid(false);
                    setIsFreqPromoApplied(false);
                }
                setPromoDescriptionMessage('');
            }
        } else if (isFreqPromoApplied) {
            setLocalPromoCode('');
            setPromoCode('');
            setPromoDescriptionMessage('');
            setIsPromoValid(false);
            setIsFreqPromoApplied(false);
        }
    }, [frequency, frequencyPromotions, isFreqPromoApplied, setPromoCode, setPromoDescriptionMessage, orderType, recurringDiscount]);

    // ─── Promo Code Handlers ───

    const handleValidatePromo = async () => {
        try {
            setIsPromoValidating(true);
            const response = await axios.get(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/validate-promo-code`,
                {
                    params: {
                        laundryId: laundryId,
                        operation: 'validatePromoCode',
                        promoCode: localPromoCode,
                        customerId: customerId || '',
                    },
                    headers: { 'x-api-key': userAuthToken },
                }
            );
            const data = response.data;
            if (data.body?.isValid) {
                setIsPromoValid(true);
                setPromoCode(localPromoCode);
                toast({
                    title: 'Promo Code Valid',
                    description: `Code "${localPromoCode}" is valid and applied.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setIsPromoValid(false);
                setLocalPromoCode('');
                setPromoCode('');
                setPromoDescriptionMessage('');
                toast({
                    title: 'Invalid Promo Code',
                    description: `Sorry, "${localPromoCode}" isn't valid or has expired.`,
                    status: 'error',
                    duration: 4000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: 'Failed to validate the promo code. Please try again later.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsPromoValidating(false);
        }
    };

    const handleEditPromo = () => {
        setIsPromoValid(false);
        setPromoCode('');
        setPromoDescriptionMessage('');
    };

    const onPromoChange = (e) => {
        const val = e.target.value;
        if (isPromoValid) {
            setIsPromoValid(false);
            setPromoCode('');
            setPromoDescriptionMessage('');
        }
        setLocalPromoCode(val);
    };

    // ─── Continue Handler (validates promo if entered but not validated) ───

    const handleContinue = () => {
        // Require frequency for recurring order types
        if ((orderType === 'frequency' || orderType === 'subscribe-save') && !frequency) {
            toast({
                title: 'Select a Frequency',
                description: 'Please choose how often you want pickup.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (!frequency && localPromoCode && !isPromoValid) {
            toast({
                title: 'Please Validate Promo',
                description: `You entered "${localPromoCode}" but haven't validated yet.`,
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        // Block a pickup on a closed / non-operating day (scheduled mode only;
        // instant Uber pickup uses today's live-window path). The server also
        // enforces this, but stop it here for a clear message.
        if (pickupMode === 'scheduled' && pickupDate && !isEligibleDay(pickupDate)) {
            toast({
                title: 'Pickup date unavailable',
                description: _closureSet.has(pickupDate)
                    ? "We're closed on the selected pickup date. Please choose another day."
                    : "We don't operate on the selected pickup date. Please choose another day.",
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        onContinue();
    };

    // ─── Uber Estimate Fetch ───

    const fetchUberEstimate = async ({ type }) => {
        const pickupAddress = address;
        const dropoffAddress = laundryAddress;
        const payload = {
            uberEnv: uberEnv,
            pickup_address: type === 'pickup' ? pickupAddress : dropoffAddress,
            dropoff_address: type === 'pickup' ? dropoffAddress : pickupAddress,
            pickup_phone: '+15125551234',
            dropoff_phone: '+15125551234',
            delivery_date: pickupDate,
            time_interval: pickupTime,
        };
        try {
            const response = await fetch(
                `${process.env.REACT_APP_AWS_API_URL}/api/uber/uberQuoteEstimate?operation=get-uber-quote&laundryId=${laundryId}`,
                { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }
            );
            const result = await response.json();
            let parsedBody = result;
            if (typeof result.body === 'string') {
                try { parsedBody = JSON.parse(result.body); } catch (e) { /* ignore */ }
            }
            if (parsedBody.estimatedFeeCents) {
                const estimate = parsedBody.estimatedFeeCents / 100;
                if (type === 'pickup') setPickupEstimate(estimate);
                else setDropoffEstimate(estimate);
            }
        } catch (error) {
            console.error('[Uber ERROR] Fetch failed:', error);
        }
    };

    useEffect(() => {
        if (pickupService === 'Uber' && pickupDate && pickupTime) fetchUberEstimate({ type: 'pickup' });
    }, [pickupService, pickupDate, pickupTime]);

    useEffect(() => {
        if (dropoffService === 'Uber' && dropoffDate && dropoffTime) fetchUberEstimate({ type: 'dropoff' });
    }, [dropoffService, dropoffDate, dropoffTime]);

    useEffect(() => {
        if (pickupMode === 'instant' && pickupService === 'Uber' && pickupDate && pickupTime) fetchUberEstimate({ type: 'pickup' });
    }, [pickupMode, pickupService, pickupDate, pickupTime]);

    // ─── Derived Values ───

    const tz = laundryTimeZone || 'America/Chicago';
    const _todayStr = getDateInTimeZone(new Date(), tz);
    const _tomorrowStr = getDateInTimeZone(addDays(new Date(), 1), tz);
    // Same-day pickup is only offered when TODAY (in the shop's timezone) is an
    // eligible pickup day AND there's still enough time before the shop closes:
    // the current time must be at least `sameDayCutoffHours` before today's slot
    // end. Uses the tenant's configured cutoff + shop timezone (not a hardcoded
    // browser hour), consistent with the same-day RETURN feasibility check.
    const sameDayPickupAllowed = (() => {
        if (!isEligibleDay(_todayStr)) return false;
        const now = toZonedTime(new Date(), tz);
        const nowHour = now.getHours() + now.getMinutes() / 60;
        const todaySlot = (deliveryTimeSlots || []).find(
            (s) => s.day === format(now, 'EEEE', { timeZone: tz })
        );
        const closeHour = todaySlot ? _parseHour(todaySlot.endTime) : null;
        if (closeHour == null) return false; // no pickup slot today
        return (closeHour - nowHour) >= (Number(sameDayCutoffHours) || 0);
    })();
    // Pickup calendar minimum: today only when same-day pickup is still allowed,
    // otherwise tomorrow (which disables today in the picker).
    const todayDate = sameDayPickupAllowed ? _todayStr : _tomorrowStr;
    // When the tenant lets customers pick time windows, both times are required.
    // When disabled (date-only), the times are auto-defaulted behind the scenes
    // (see the auto-select effects) and the customer only needs valid dates.
    const dryCleanDateOk = !dryCleanMinDate || !!dryCleanDropoffDate;
    const isFormValid = customerTimeSelectionEnabled
        ? (pickupDate && pickupTime && dropoffDate && dropoffTime && dryCleanDateOk)
        : (pickupDate && dropoffDate && dryCleanDateOk);

    const repeatDiscount = Number(recurringDiscount) || 0;
    const repeatDiscountLabel = Number.isInteger(repeatDiscount)
        ? String(repeatDiscount)
        : String(repeatDiscount);
    const repeatChoices = sortFrequencies(laundryFrequency);
    const matchedFrequencyPromo = frequency && frequencyPromotions
        ? frequencyPromotions.find((p) => p.frequency === frequency)
        : null;

    // ─── Render ───

    return (
        <Stack spacing={5} width="100%" maxW="500px" mx="auto" py={2}>
            {/* ─── Service-aware scheduling guidance ───
                The calendar computes the earliest valid return from the SLOWEST
                service in the cart (its turnaround), then honors operating days,
                closures, and service zones. This note tells the customer to pick
                their service first so the dates reflect the speed they chose. */}
            <Box
                bg="blue.50"
                borderRadius="xl"
                p={{ base: 4, md: 5 }}
                border="1px solid"
                borderColor="blue.100"
            >
                <Text fontSize="sm" fontWeight="700" color="blue.800" mb={1}>
                    When your laundry comes back
                </Text>
                <Text fontSize="xs" color="blue.700" lineHeight="1.5">
                    {(() => {
                        if (!_hasTurnaround) return 'Your laundry is returned as early as the day after pickup.';
                        const hrs = Number(minTurnaroundHours);
                        if (hrs <= 0) return 'Same-day service: your laundry can be returned on your pickup day.';
                        if (hrs <= 24) return `Your laundry is returned the day after pickup (${hrs}-hour turnaround).`;
                        const days = Math.ceil(hrs / 24);
                        return `Your laundry is returned ${days} days after pickup (${hrs}-hour turnaround).`;
                    })()}
                    {' '}Based on the services in your order. Dates are subject to route availability
                    and may be adjusted when necessary.
                </Text>
            </Box>

            {/* ─── Pickup Type ─── */}
            {(uberExists || doordashExists) && (
                <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontSize={['xs', 'md', 'lg']} fontWeight="semibold" mb={3}>
                        Pickup Type
                    </Text>
                    <RadioGroup value={pickupMode} onChange={setPickupMode} colorScheme="blue">
                        <Stack spacing={3}>
                            {[
                                {
                                    value: 'instant',
                                    title: 'On-demand pickup',
                                    subtitle: canDoInstantPickup
                                        ? `A courier picks up today, usually within 1–2 hours${bothCouriers ? '' : ` (powered by ${uberExists ? 'Uber' : 'DoorDash'})`}.`
                                        : onDemandUnavailableReason,
                                    disabled: !canDoInstantPickup,
                                },
                                {
                                    value: 'scheduled',
                                    title: 'Scheduled pickup',
                                    subtitle: 'Choose a day and time window that works for you.',
                                    disabled: false,
                                },
                            ].map((opt) => {
                                const selected = pickupMode === opt.value;
                                return (
                                    <Box
                                        key={opt.value}
                                        onClick={() => { if (!opt.disabled && !selected) setPickupMode(opt.value); }}
                                        p={3}
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor={selected ? 'blue.400' : 'gray.200'}
                                        bg={selected ? 'blue.50' : opt.disabled ? 'gray.50' : 'white'}
                                        cursor={opt.disabled ? 'not-allowed' : 'pointer'}
                                        opacity={opt.disabled ? 0.7 : 1}
                                    >
                                        <Radio value={opt.value} isDisabled={opt.disabled} alignItems="flex-start">
                                            <Text fontWeight="semibold">{opt.title}</Text>
                                            <Text fontSize="sm" color="gray.600">{opt.subtitle}</Text>
                                        </Radio>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </RadioGroup>

                    {pickupMode === 'instant' && bothCouriers && (
                        <Box mt={4}>
                            <Text fontSize="sm" fontWeight="semibold" mb={2}>Choose your courier</Text>
                            <RadioGroup
                                value={instantCourier}
                                onChange={(courier) => {
                                    setInstantCourier(courier);
                                    setPickupService(courier);
                                }}
                                colorScheme="blue"
                            >
                                <HStack spacing={6} flexWrap="wrap">
                                    <Radio value="Uber">Uber</Radio>
                                    <Radio value="DoorDash">DoorDash</Radio>
                                </HStack>
                            </RadioGroup>
                        </Box>
                    )}
                    {pickupMode === 'instant' && instantCourier === 'Uber' && pickupEstimate && (
                        <Text fontSize="sm" color="blue.600" mt={2}>
                            Estimated Uber pickup fee: ${pickupEstimate.toFixed(2)}
                        </Text>
                    )}
                    {pickupMode === 'instant' && instantCourier === 'DoorDash' && (
                        <Text fontSize="sm" color="gray.600" mt={2}>
                            A DoorDash driver will pick up your laundry. You'll get a tracking link.
                        </Text>
                    )}
                </Box>
            )}

            {/* ─── Pickup Date/Time (scheduled mode) ─── */}
            {pickupMode === 'scheduled' && (
                <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontSize="sm" fontWeight="600" color="blue.600" mb={3}>
                        Pickup
                    </Text>
                    <Stack direction={{ base: 'column', md: 'row' }} spacing={4} width="100%">
                        <FormControl id="pickupDate" isRequired width="100%">
                            <FormLabel fontSize={['md', 'lg']}>Pickup Date</FormLabel>
                            <Input
                                type="date"
                                min={todayDate}
                                value={pickupDate}
                                onChange={(e) => setPickupDate(e.target.value || todayDate)}
                            />
                        </FormControl>
                        {customerTimeSelectionEnabled && (
                            <FormControl id="pickupTime" isRequired width="100%">
                                <FormLabel fontSize={['md', 'lg']}>Pickup Time</FormLabel>
                                <Select
                                    value={pickupTime}
                                    placeholder="Select Pickup Time Slot"
                                    onChange={(e) => setPickupTime(e.target.value)}
                                    isDisabled={pickupDate && getAvailableTimeSlots(pickupDate).length === 0}
                                >
                                    {getAvailableTimeSlots(pickupDate).map((timeSlot, index) => (
                                        <option key={index} value={timeSlot}>
                                            {slotLabel(timeSlot)}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    </Stack>
                    {/* Date-only tenants: customer picks the date; the operator
                        sets routes and notifies the pickup time by text/email. */}
                    {!customerTimeSelectionEnabled && (
                        <Text fontSize="sm" color="gray.500" mt={3}>
                            We'll confirm your pickup time by text or email once your route is scheduled.
                        </Text>
                    )}
                    {/* Closed/holiday date chosen (operating weekday but a closure
                        date) — the time-slot check below won't catch this. */}
                    {pickupDate && _closureSet.has(pickupDate) && (
                        <Text fontSize="sm" color="orange.600" fontWeight="600" mt={3}>
                            We're closed on this date. Please select another pickup date.
                        </Text>
                    )}
                    {/* Explain why Continue is blocked when the chosen date has no
                        pickup windows (e.g. a day the tenant doesn't operate). */}
                    {pickupDate && !_closureSet.has(pickupDate) && getAvailableTimeSlots(pickupDate).length === 0 && (
                        <Text fontSize="sm" color="orange.600" fontWeight="600" mt={3}>
                            Pickup is not available on this date. Please select another pickup date.
                        </Text>
                    )}
                    {/* Zone-restricted day: the date is a normal operating day and
                        not a closure, but the customer's area isn't served that day. */}
                    {pickupDate && !_closureSet.has(pickupDate) && getAvailableTimeSlots(pickupDate).length > 0 && !isEligibleDay(pickupDate) && (
                        <Text fontSize="sm" color="orange.600" fontWeight="600" mt={3}>
                            We don't pick up in your area on this day. Please choose another pickup date.
                        </Text>
                    )}
                </Box>
            )}

            {/* ─── Pickup Service Choice ─── */}
            {pickupMode === 'scheduled' && (
                <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontSize={['xs', 'md', 'lg']} fontWeight="semibold" mb={2}>
                        Choose Pickup Service
                    </Text>
                    <RadioGroup
                        onChange={setPickupService}
                        value={pickupService || 'LaundryDriver'}
                        colorScheme="blue"
                    >
                        <HStack spacing={6} flexWrap="wrap">
                            {uberExists && <Radio value="Uber">Uber</Radio>}
                            {doordashExists && <Radio value="DoorDash">DoorDash</Radio>}
                            <Radio value="LaundryDriver">Laundry&nbsp;Driver</Radio>
                        </HStack>
                    </RadioGroup>
                    {pickupService === 'DoorDash' && (
                        <Text fontSize="sm" color="red.600" mt={1}>
                            🚗 A DoorDash driver will pick up your laundry. You'll get a tracking link.
                        </Text>
                    )}
                    {pickupService === 'Uber' && pickupEstimate && (
                        <Text fontSize="sm" color="blue.600" mt={1}>
                            🚕 Estimated Uber Pickup Fee: ${pickupEstimate.toFixed(2)}
                        </Text>
                    )}
                    {pickupService === 'LaundryDriver' && (
                        <Text fontSize="sm" color="green.600" mt={1}>
                            ✅ Pickup by our driver — any delivery fee is shown at checkout
                        </Text>
                    )}
                    {(pickupService || 'LaundryDriver') === 'LaundryDriver' && missedPickupFee > 0 && (
                        <Box mt={3} p={3} bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md">
                            <Text fontSize="sm" fontWeight="semibold" color="orange.800">
                                Missed pickup fee: ${Number(missedPickupFee).toFixed(2)}
                            </Text>
                            <Text fontSize="sm" color="orange.800">
                                Please have your laundry ready and easy to reach during your pickup window. If our
                                driver arrives and cannot complete the pickup, a ${Number(missedPickupFee).toFixed(2)} fee
                                is charged and you can choose a new pickup time.
                            </Text>
                        </Box>
                    )}
                </Box>
            )}

            {/* ─── Dropoff Date/Time ─── */}
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                <Text fontSize="sm" fontWeight="600" color="blue.600" mb={3}>
                    Dropoff
                </Text>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} width="100%">
                    <FormControl id="dropoffDate" isRequired width="100%">
                        <FormLabel fontSize={['md', 'lg']}>{dropoffDateLabel}</FormLabel>
                        <Input
                            type="date"
                            min={minDropoffDateStr(pickupDate)}
                            value={dropoffDate}
                            onChange={(e) =>
                                setDropoffDate(
                                    e.target.value ||
                                    minDropoffDateStr(pickupDate)
                                )
                            }
                        />
                    </FormControl>
                    {customerTimeSelectionEnabled && (
                        <FormControl id="dropoffTime" isRequired width="100%">
                            <FormLabel fontSize={['md', 'lg']}>Drop-off Time</FormLabel>
                            <Select
                                value={dropoffTime}
                                placeholder="Select Drop-off Time Slot"
                                onChange={(e) => setDropoffTime(e.target.value)}
                                isDisabled={dropoffDate && getAvailableTimeSlots(dropoffDate).length === 0}
                            >
                                {getAvailableTimeSlots(dropoffDate).map((timeSlot, index) => (
                                    <option key={index} value={timeSlot}>
                                        {slotLabel(timeSlot)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Stack>
                {!customerTimeSelectionEnabled && (
                    <Text fontSize="sm" color="gray.500" mt={3}>
                        We'll confirm your delivery time by text or email once your route is scheduled.
                    </Text>
                )}
                {customerTimeSelectionEnabled && dropoffDate && getAvailableTimeSlots(dropoffDate).length === 0 && (
                    <Text fontSize="sm" color="orange.600" fontWeight="600" mt={3}>
                        Drop-off is not available on this date. Please select another drop-off date.
                    </Text>
                )}
                {_hasTurnaround && minDropoffDays === 0 && (
                    <Text fontSize="xs" color="gray.500" mt={2}>
                        Same-day service: your order can be returned on the pickup date.
                    </Text>
                )}
                {sameDayBlocked && (
                    <Text fontSize="sm" color="orange.600" fontWeight="600" mt={2}>
                        Same-day return isn't available for this pickup time — there isn't enough time before we close. Please choose an earlier pickup window, or your order will be returned the next day.
                    </Text>
                )}
                {dryCleanMinDate && (
                    <FormControl id="dryCleanDropoffDate" isRequired mt={4}>
                        <FormLabel fontSize={['md', 'lg']}>Dry cleaning back</FormLabel>
                        <Input
                            type="date"
                            min={dryCleanMinDate}
                            value={dryCleanDropoffDate}
                            onChange={(e) => {
                                const next = e.target.value;
                                if (setDryCleanDropoffDate) {
                                    setDryCleanDropoffDate(!next || next < dryCleanMinDate ? dryCleanMinDate : next);
                                }
                            }}
                        />
                        <Text fontSize="xs" color="gray.500" mt={2}>
                            Dry cleaning follows the cleaner's schedule, so it can come back on a different day from the wash and fold. You can choose a later day.
                        </Text>
                    </FormControl>
                )}
                {!sameDayBlocked && !earliestDropoffDate && minDropoffDays >= 1 && pickupDate && (() => {
                    // Honest earliest-return note. The raw turnaround is pickup +
                    // minDropoffDays; the true earliest can be later when those
                    // days aren't delivery days (e.g. a Wed drop at a Mon–Thu shop
                    // rolls to the following Monday). Show the actual date and, when
                    // it was pushed out, explain why so the gap isn't a surprise.
                    const rawStr = getDateInTimeZone(addDays(new Date(`${pickupDate}T00:00:00`), minDropoffDays), laundryTimeZone || 'America/Chicago');
                    const earliest = minDropoffDateStr(pickupDate);
                    const pushed = earliest !== rawStr;
                    const fmt = (s) => format(toZonedTime(new Date(`${s}T00:00`), laundryTimeZone || 'America/Chicago'), 'EEE, MMM d', { timeZone: laundryTimeZone || 'America/Chicago' });
                    return (
                        <Text fontSize="xs" color="gray.500" mt={2}>
                            {pushed
                                ? `Based on your services and our delivery days, the earliest return is ${fmt(earliest)}.`
                                : `Based on the services in your order, the earliest return is ${fmt(earliest)}.`}
                        </Text>
                    );
                })()}
            </Box>

            {/* ─── Dropoff Service Choice ─── */}
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                <Text fontSize={['xs', 'md', 'lg']} fontWeight="semibold" mb={2}>
                    Choose Dropoff Service
                </Text>
                <RadioGroup
                    onChange={setDropoffService}
                    value={dropoffService || 'LaundryDriver'}
                    colorScheme="blue"
                >
                    <HStack spacing={6} flexWrap="wrap">
                        {uberExists && <Radio value="Uber">Uber</Radio>}
                        {doordashExists && <Radio value="DoorDash">DoorDash</Radio>}
                        <Radio value="LaundryDriver">Laundry&nbsp;Driver</Radio>
                    </HStack>
                </RadioGroup>
                {dropoffService === 'DoorDash' && (
                    <Text fontSize="sm" color="red.600" mt={1}>
                        🚗 A DoorDash driver will deliver your laundry. You'll get a tracking link.
                    </Text>
                )}
                {dropoffService === 'Uber' && dropoffEstimate && (
                    <Text fontSize="sm" color="blue.600" mt={1}>
                        🚕 Estimated Uber Dropoff Fee: ${dropoffEstimate.toFixed(2)}
                    </Text>
                )}
                {dropoffService === 'LaundryDriver' && (
                    <Text fontSize="sm" color="green.600" mt={1}>
                        ✅ Delivery by our driver — any delivery fee is shown at checkout
                    </Text>
                )}
            </Box>

            {/* Repeat pickup. Weekly starts selected. One-time stays a quiet opt-out. */}
            {repeatChoices.length > 0 && (
                <Box>
                    <Text fontWeight="700" fontSize="xl" color="gray.900" mb={3}>
                        Make it regular
                    </Text>
                    {repeatDiscount > 0 && (
                        <Flex
                            align="center"
                            gap={2}
                            px={3}
                            py={2.5}
                            mb={3}
                            border="1px solid"
                            borderColor="blue.500"
                            borderRadius="lg"
                            color="blue.700"
                        >
                            <Box as={FiTag} flexShrink={0} />
                            <Text fontSize="sm" fontWeight="500">
                                Save {repeatDiscountLabel}% by making it a repeat pickup
                            </Text>
                        </Flex>
                    )}
                    <SimpleGrid columns={2} spacing={3}>
                        {repeatChoices.map((opt) => {
                            const selected = orderType !== 'one-time' && frequency === opt;
                            return (
                                <Button
                                    key={opt}
                                    h="52px"
                                    borderRadius="lg"
                                    fontWeight="600"
                                    bg={selected ? 'blue.500' : 'white'}
                                    color={selected ? 'white' : 'gray.800'}
                                    border="1px solid"
                                    borderColor={selected ? 'blue.500' : 'gray.200'}
                                    _hover={{ bg: selected ? 'blue.600' : 'gray.50' }}
                                    _active={{ bg: selected ? 'blue.700' : 'gray.100' }}
                                    onClick={() => {
                                        if (orderType === 'subscribe-save') {
                                            setFrequency(opt);
                                            return;
                                        }
                                        if (setOrderType) setOrderType('frequency', opt);
                                        else setFrequency(opt);
                                    }}
                                >
                                    {frequencyLabel(opt)}
                                </Button>
                            );
                        })}
                    </SimpleGrid>
                    <Button
                        variant="link"
                        size="sm"
                        mt={3}
                        w="100%"
                        fontWeight={orderType === 'one-time' ? '700' : '500'}
                        color={orderType === 'one-time' ? 'gray.800' : 'gray.500'}
                        onClick={() => setOrderType && setOrderType('one-time')}
                    >
                        Just this once
                    </Button>
                    {subscribeSaveAvailable && cartIsBagOnly && (
                        <Button
                            variant="link"
                            size="sm"
                            mt={1}
                            w="100%"
                            fontWeight={orderType === 'subscribe-save' ? '700' : '500'}
                            color={orderType === 'subscribe-save' ? 'green.700' : 'gray.500'}
                            onClick={() => setOrderType && setOrderType('subscribe-save')}
                        >
                            Subscribe &amp; save{subscriptionDiscount > 0 ? ` ${subscriptionDiscount}%` : ''} on a bag plan
                        </Button>
                    )}
                    {orderType === 'subscribe-save' && subscriptionDiscount > 0 && (
                        <Text fontSize="xs" color="green.700" textAlign="center" mt={2}>
                            Bag plan: {subscriptionDiscount}% off every order
                        </Text>
                    )}
                    {orderType !== 'one-time' && !frequency && (
                        <Text fontSize="xs" color="red.500" mt={2}>
                            Choose how often to continue
                        </Text>
                    )}
                    {orderType !== 'subscribe-save' && repeatDiscount <= 0 && matchedFrequencyPromo && (
                        <Text fontSize="xs" color="green.700" textAlign="center" mt={2}>
                            {matchedFrequencyPromo.description || `You'll save on every ${frequencyLabel(frequency).toLowerCase()} order.`}
                        </Text>
                    )}
                </Box>
            )}

            {/* ─── Recurring order courier (Uber / DoorDash) ─── */}
            {frequency && (uberExists || doordashExists) && (
                <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <Text fontSize={['xs', 'md', 'lg']} fontWeight="semibold" mb={2}>
                        Recurring Order Delivery Partner
                    </Text>
                    {[
                        { label: 'Who should pick up your recurring orders?', uber: uberPickupFrequency, dd: doordashPickupFrequency,
                          setUber: setUberPickupFrequency, setDd: setDoordashPickupFrequency, name: 'pickup' },
                        { label: 'Who should deliver your recurring orders?', uber: uberDropoffFrequency, dd: doordashDropoffFrequency,
                          setUber: setUberDropoffFrequency, setDd: setDoordashDropoffFrequency, name: 'dropoff' },
                    ].map((leg) => (
                        <FormControl key={leg.name} mb={2}>
                            <FormLabel fontSize={['md', 'lg']}>{leg.label}</FormLabel>
                            <RadioGroup
                                onChange={(value) => { leg.setUber(value === 'Uber'); leg.setDd(value === 'DoorDash'); }}
                                value={leg.uber ? 'Uber' : leg.dd ? 'DoorDash' : 'LaundryDriver'}
                                colorScheme="blue"
                            >
                                <HStack spacing={6} flexWrap="wrap">
                                    <Radio value="LaundryDriver">Laundry&nbsp;Driver</Radio>
                                    {uberExists && <Radio value="Uber">Uber</Radio>}
                                    {doordashExists && <Radio value="DoorDash">DoorDash</Radio>}
                                </HStack>
                            </RadioGroup>
                            {leg.uber && (
                                <Text mt={1} fontSize="sm" color="gray.600">
                                    Charges will apply based on the pickup date and time of frequency order.
                                </Text>
                            )}
                        </FormControl>
                    ))}
                </Box>
            )}

            {/* ─── Promo Code ─── */}
            {!frequency && (
                <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                    <FormControl id="promoCode">
                        <FormLabel fontSize={['md', 'lg']}>Promotion</FormLabel>
                        <Flex direction="row" align="center" wrap="nowrap">
                            <Input
                                placeholder="Enter promo code"
                                value={localPromoCode}
                                onChange={onPromoChange}
                                isDisabled={isPromoValid}
                                flex="1"
                                mr={2}
                                minWidth={0}
                            />
                            <Button
                                ml={2}
                                onClick={isPromoValid ? handleEditPromo : handleValidatePromo}
                                isLoading={isPromoValidating}
                                colorScheme={isPromoValid ? 'yellow' : 'blue'}
                                flexShrink={0}
                                size={['md', 'lg']}
                            >
                                {isPromoValid ? 'Edit' : 'Validate'}
                            </Button>
                        </Flex>
                    </FormControl>
                </Box>
            )}

            {/* ─── Promo Description Banner ─── */}
            {promoDescriptionMessage && (
                <Box
                    p={3}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="blue.300"
                    bg="blue.50"
                    color="blue.700"
                    fontSize={['sm', 'md']}
                >
                    {promoDescriptionMessage}
                </Box>
            )}

            {/* ─── Special Instructions ─── */}
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                <FormControl id="specialInstructions">
                    <FormLabel fontSize={['md', 'lg']}>Special Instructions</FormLabel>
                    <Textarea
                        placeholder="Specify any preferences like detergent type, folding style, or stain treatment."
                        value={specialInstructions}
                        onChange={(e) => {
                            const newVal = e.target.value;
                            setSpecialInstructions(newVal);
                            if (setSaveSpecialInstructions) {
                                if (newVal !== initialSpecialInstructionsRef.current && newVal.trim() !== '') {
                                    setSaveSpecialInstructions(true);
                                } else {
                                    setSaveSpecialInstructions(false);
                                }
                            }
                        }}
                        size="sm"
                        fontSize="16px"
                        borderRadius="lg"
                        rows={3}
                        resize="none"
                    />
                </FormControl>
            </Box>

            {/* ─── Laundry Bags ─── */}
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm" border="1px solid" borderColor="gray.100">
                <FormControl id="laundryBags" isRequired width="100%">
                    <FormLabel fontSize={['md', 'lg']}>Laundry Bags</FormLabel>
                    <HStack spacing={3}>
                        <Button
                            size="lg"
                            variant="outline"
                            colorScheme="blue"
                            borderRadius="xl"
                            aria-label="Fewer bags"
                            onClick={() => setLaundryBags(Math.max(1, (Number(laundryBags) || 1) - 1))}
                            isDisabled={(Number(laundryBags) || 1) <= 1}
                        >
                            −
                        </Button>
                        <NumberInput
                            max={50}
                            min={1}
                            step={1}
                            value={laundryBags}
                            precision={0}
                            onChange={(bags) => setLaundryBags(Number(bags))}
                            w="88px"
                        >
                            <NumberInputField inputMode="numeric" textAlign="center" fontWeight="700" fontSize="lg" h="48px" px={2} />
                        </NumberInput>
                        <Button
                            size="lg"
                            variant="outline"
                            colorScheme="blue"
                            borderRadius="xl"
                            aria-label="More bags"
                            onClick={() => setLaundryBags(Math.min(50, (Number(laundryBags) || 0) + 1))}
                            isDisabled={(Number(laundryBags) || 0) >= 50}
                        >
                            +
                        </Button>
                    </HStack>
                </FormControl>
            </Box>

            {/* ─── Navigation Buttons ─── */}
            <HStack spacing={4} width="100%">
                <Button
                    variant="outline"
                    colorScheme="gray"
                    onClick={onBack}
                    size={['md', 'lg']}
                    flex="1"
                >
                    Back
                </Button>
                <Button
                    colorScheme="blue"
                    onClick={handleContinue}
                    isDisabled={!isFormValid}
                    size={['md', 'lg']}
                    flex="2"
                >
                    Continue
                </Button>
            </HStack>
        </Stack>
    );
}
